import { Navigate, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { canAccessCoportalPage } from './visibility';
import {
  COPORTAL_FREE_PAGES,
  COPORTAL_GATED_SLUGS,
  canAccessCoportalRoute,
  coportalSlugFromPath,
  isCoportalAliasRoute,
  isGatedCoportalPage,
} from './coportalPages';

/**
 * CS/OPS Portal page gate — the portal-flavoured sibling of
 * {@link RequirePage}.
 *
 * The light modules (`it`, `finance`, `ecommerce`, `bizdev`) each wrap their
 * routes in `<RequirePage>`, which speaks `{module}:{slug}` tokens. Portal
 * pages are stored BARE, so `RequirePage` cannot read them and the portal
 * shipped with no page gate at all: a user restricted to three pages could
 * still open all fifty.
 *
 * One guard around the whole subtree rather than 60 per-route wrappers — the
 * slug comes from the URL, so a route added later is covered automatically
 * (and, if it is not a rail page, falls through by design; see
 * `coportalPages.ts` for why the scope is deliberately narrow).
 *
 * Behaviour matches `RequirePage`: allowed → render; denied → redirect to the
 * first page the user CAN see, in rail order; nothing reachable → /forbidden.
 * The redirect (rather than a bare 403) is what PHP's module bootstrap does,
 * and it is what keeps a restricted user's bookmark from dead-ending.
 */
export function RequireCoportalPage({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const slug = coportalSlugFromPath(pathname);

  // Not a page this client gates (alias, hidden child, help/about/theme…).
  // The API remains the authority for those.
  // Alias routes (one URL fronting several grants, e.g. Hotline WhatsApp
  // MY + SG) pass when any of their pages does.
  if (!isGatedCoportalPage(slug) && !isCoportalAliasRoute(slug)) return <>{children}</>;

  if (canAccessCoportalRoute(slug, user)) {
    return <>{children}</>;
  }

  const fallback = COPORTAL_GATED_SLUGS.find((s) =>
    canAccessCoportalPage(s, COPORTAL_FREE_PAGES.has(s), user),
  );
  if (!fallback) return <Navigate to="/forbidden" replace />;
  return <Navigate to={`/coportal/${fallback}`} replace />;
}
